/**
 * User-Settings API Service (NC-505 Phase 5 PR-C).
 *
 * Per-user notification settings: Teams webhook URL + image-build toggle.
 * GET always returns a stable doc shape (server falls back to defaults for
 * first-time users); PUT is a partial upsert — send `teamsWebhookUrl: null`
 * to clear the URL.
 */

import apiClient from './api';
import { ApiResponse, UserSettingsDoc, UserSettingsInput, WebhookTestResult } from './types';

export const userSettingsApi = {
  /** Read the authenticated caller's settings doc (default-shaped if never saved). */
  async get(): Promise<UserSettingsDoc> {
    const response = await apiClient.get<ApiResponse<UserSettingsDoc>>('/user-settings');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch user settings');
    }
    return response.data.data;
  },

  /** Partial upsert of the caller's own settings doc. */
  async update(input: UserSettingsInput): Promise<UserSettingsDoc> {
    const response = await apiClient.put<ApiResponse<UserSettingsDoc>>('/user-settings', input);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to update user settings');
    }
    return response.data.data;
  },

  /**
   * Server-side test POST against the SAVED webhook URL (the browser cannot
   * reach the webhook directly — CORS). 400 = no URL saved, 502 = webhook
   * unreachable / rejected the card.
   */
  async testWebhook(): Promise<WebhookTestResult> {
    const response = await apiClient.post<ApiResponse<WebhookTestResult>>(
      '/user-settings/test-notification'
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Webhook test failed');
    }
    return response.data.data;
  },
};
